import {Fade, IconButton} from '@chakra-ui/react';
import {ArrowUp} from 'lucide-react';
import {FC, useCallback, useEffect, useRef, useState} from 'react';

const SCROLL_OFFSET_PX = 500;

const isBrowser = () => typeof window !== 'undefined';

interface IProps {
  isFromHomePage?: boolean;
  isDesktop?: boolean;
  isMetro?: boolean;
}

export const ScrollToTopButton: FC<IProps> = ({isFromHomePage, isDesktop, isMetro}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [rightOffset, setRightOffset] = useState<number>(8);
  const lastScrollY = useRef(0);

  const computeRightOffset = useCallback(() => {
    if (!isBrowser()) return 8;

    const GAP_PX = 50;
    const ASIDE_WIDTH_PX = 350;
    const CONTAINER_MAX_PX = 1800;
    const vw = window.innerWidth;

    if (!isDesktop) return 8;

    if (vw > CONTAINER_MAX_PX) {
      return Math.round((vw - CONTAINER_MAX_PX) / 2 + ASIDE_WIDTH_PX + GAP_PX);
    }
    return ASIDE_WIDTH_PX + GAP_PX;
  }, [isDesktop]);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > SCROLL_OFFSET_PX && currentScrollY < lastScrollY.current) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    lastScrollY.current = currentScrollY;
  };

  useEffect(() => {
    if (!isBrowser()) return;
    if (!isDesktop) return;
    if (!isMetro) return;
    setRightOffset(computeRightOffset());

    const handleResize = () => setRightOffset(computeRightOffset());
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [computeRightOffset, isMetro, isDesktop]);

  useEffect(() => {
    if (!isBrowser()) return;

    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClick = () => {
    if (!isBrowser()) return;
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  return isVisible ? (
    <Fade in={isVisible}>
      <IconButton
        aria-label="Scroll to top"
        position="fixed"
        bottom={[isFromHomePage ? 32 : 20, 8]}
        right={[5, rightOffset]}
        zIndex="overlay"
        size="lg"
        colorScheme="pink"
        borderRadius="full"
        icon={<ArrowUp size={24} />}
        boxShadow="lg"
        _hover={{transform: 'translateY(-2px)'}}
        onClick={handleClick}
      />
    </Fade>
  ) : null;
};
